import { Keys, OrderResponseMessage, TempOrder } from '@eih-netbroker/common';
import { useMemo } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { NbButton } from '../../../../shared/components/button/Button';
import { useOrderState } from '../OrderStateContext';
import classes from './OrderModal.module.scss';

export interface OrderModalBottomNavigationProps {
  isLoading: boolean;
  tempOrder?: TempOrder;
  warnings?: OrderResponseMessage[];
  onContinue: () => void;
  onBack: () => void;
  onClose: () => void;
  initialView?: 'form' | 'confirm';
}

export const OrderModalBottomNavigation = ({
  onContinue,
  onBack,
  tempOrder,
  warnings,
  isLoading,
  initialView = 'form',
  onClose,
}: OrderModalBottomNavigationProps) => {
  const { translation } = useLocale();
  const { smsCode, smsCodeError } = useOrderState();
  const { calculatedCostState } = useOrderState();

  const primaryButton = useMemo(() => {
    if (warnings && warnings?.some(message => message.msgType === 'error')) {
      return (
        <NbButton
          isLoading={isLoading}
          onClick={onBack}
        >
          {translation.general.back}
        </NbButton>
      );
    }

    if (warnings) {
      return (
        <NbButton
          isLoading={isLoading}
          onClick={onContinue}
        >
          {translation.general.continue}
        </NbButton>
      );
    }

    if (tempOrder?.showWarningConfirmation && tempOrder.messages?.some(message => message.msgType === 'error')) {
      return (
        <NbButton
          isLoading={isLoading}
          onClick={onBack}
        >
          {translation.general.back}
        </NbButton>
      );
    }

    if (tempOrder?.showWarningConfirmation) {
      return (
        <NbButton
          isLoading={isLoading}
          onClick={onContinue}
        >
          {translation.general.continue}
        </NbButton>
      );
    }

    if (tempOrder?.showConfirmation) {
      return (
        <NbButton
          data-testid="order-confirm-confirm-button"
          disabled={calculatedCostState?.isLoading || calculatedCostState?.hasError}
          isLoading={isLoading}
          onClick={onContinue}
        >
          {translation.general.confirm}
        </NbButton>
      );
    }

    if (
      tempOrder?.showSmsConfirmation &&
      smsCodeError?.errorCode !== Keys.ErrorCode.twoFactor.orderSmsConfirmationError
    ) {
      return (
        <NbButton
          disabled={smsCode?.length !== 4}
          isLoading={isLoading}
          onClick={onContinue}
        >
          {translation.orderModal.twoFactorConfirm.confirm}
        </NbButton>
      );
    }

    if (
      tempOrder?.showSmsConfirmation &&
      smsCodeError?.errorCode === Keys.ErrorCode.twoFactor.orderSmsConfirmationError
    ) {
      return <NbButton onClick={onClose}>{translation.general.ok}</NbButton>;
    }
  }, [
    tempOrder,
    translation,
    isLoading,
    onContinue,
    onBack,
    warnings,
    smsCode,
    calculatedCostState,
    smsCodeError?.errorCode,
    onClose,
  ]);

  const secondaryButton = useMemo(() => {
    if (
      tempOrder?.showSmsConfirmation &&
      smsCodeError?.errorCode === Keys.ErrorCode.twoFactor.orderSmsConfirmationError &&
      initialView === 'form'
    ) {
      return (
        <NbButton
          disabled={isLoading}
          onClick={onBack}
        >
          {translation.orderModal.twoFactorConfirm.restart}
        </NbButton>
      );
    }

    if (
      !(
        warnings?.some(message => message.msgType === 'error') ||
        tempOrder?.messages?.some(m => m.msgType === 'error') ||
        smsCodeError?.errorCode === Keys.ErrorCode.twoFactor.orderSmsConfirmationError
      )
    ) {
      return (
        <NbButton
          color="secondary"
          data-testid={tempOrder?.showConfirmation ? 'order-confirm-back-button' : undefined}
          disabled={isLoading}
          onClick={onBack}
        >
          {tempOrder?.showSmsConfirmation ? translation.general.cancel : translation.general.back}
        </NbButton>
      );
    }

    // Return empty div so the primary button will float to the right
    return <div></div>;
  }, [tempOrder, translation, onBack, warnings, initialView, smsCodeError, isLoading]);

  return (
    <div className={classes.orderButtons}>
      {secondaryButton}
      {primaryButton}
    </div>
  );
};
